import { extractCogQuery } from '@/utils/titilerRasterProfile';

export type PathProfileCoordinate = readonly [number, number];

export interface PathProfileSample {
  readonly distance: number;
  readonly elevation: number;
  readonly lon: number;
  readonly lat: number;
}

const EARTH_RADIUS_M = 6_371_000;

export function pathProfileDistance(
  a: PathProfileCoordinate,
  b: PathProfileCoordinate,
): number {
  const toRad = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/** Distribute samples by travelled distance across every segment in the path. */
export function interpolatePathProfile(
  vertices: readonly PathProfileCoordinate[],
  sampleCount = 64,
): Array<{ distance: number; lon: number; lat: number }> {
  if (vertices.length < 2) return [];

  const cumulative = [0];
  for (let i = 1; i < vertices.length; i += 1) {
    cumulative.push(
      cumulative[i - 1] + pathProfileDistance(vertices[i - 1], vertices[i]),
    );
  }
  const total = cumulative[cumulative.length - 1];
  if (!(total > 0)) return [];

  const count = Math.max(2, Math.round(sampleCount));
  return Array.from({ length: count }, (_, index) => {
    const distance = (total * index) / (count - 1);
    let segment = 0;
    while (
      segment < cumulative.length - 2
      && cumulative[segment + 1] < distance
    ) {
      segment += 1;
    }
    const segmentStart = cumulative[segment];
    const segmentLength = cumulative[segment + 1] - segmentStart;
    const t = segmentLength > 0 ? (distance - segmentStart) / segmentLength : 0;
    const start = vertices[segment];
    const end = vertices[segment + 1];
    return {
      distance,
      lon: start[0] + (end[0] - start[0]) * t,
      lat: start[1] + (end[1] - start[1]) * t,
    };
  });
}

export async function fetchPathProfile(
  tilesUrl: string,
  vertices: readonly PathProfileCoordinate[],
  options: {
    readonly sampleCount?: number;
    readonly fetcher?: typeof fetch;
    readonly signal?: AbortSignal;
  } = {},
): Promise<PathProfileSample[]> {
  const parts = extractCogQuery(tilesUrl);
  if (!parts) throw new Error('This raster does not expose a TiTiler COG source.');

  const locations = interpolatePathProfile(vertices, options.sampleCount);
  if (locations.length === 0) throw new Error('Draw at least two different points.');
  const request = options.fetcher ?? globalThis.fetch.bind(globalThis);

  const results = await Promise.all(
    locations.map(async (location): Promise<PathProfileSample | null> => {
      const response = await request(
        `${parts.base}/cog/point/${location.lon},${location.lat}?url=${parts.cogParam}`,
        options.signal ? { signal: options.signal } : undefined,
      );
      if (!response.ok) return null;
      const payload = (await response.json()) as { values?: unknown[] };
      const elevation = Number(payload.values?.[0]);
      if (!Number.isFinite(elevation)) return null;
      return { ...location, elevation };
    }),
  );

  const samples = results.filter((sample): sample is PathProfileSample => sample !== null);
  if (samples.length === 0) {
    throw new Error('No height or depth values were found along this path.');
  }
  return samples;
}
